import React, { FocusEventHandler, forwardRef, useCallback, useEffect, useMemo } from 'react'
import { useDebounce } from 'react-use'
import {
  useAutocompleteLocation,
  ILocationSearchItemFragment,
  ILocationSearchQueryVariables,
  IUseAutocompleteLocationProps,
} from 'transfix-maps/hooks/useAutocompleteLocation'
import AutocompleteBase, {
  IAutocompleteBaseOption,
  TAutocompleteBaseChangeHandler,
  isAutocompleteBaseOption,
  useAutocompleteInputState,
  TInputChangeHandler,
  TAutocompleteBaseSharedProps,
} from 'transfix-ui/components/AutocompleteBase'
import { AutocompleteClassKey as MuiAutocompleteClassKey } from 'transfix-ui/core'
import { ClassNameMap } from 'transfix-ui/core/styles/withStyles'
import { IGeocodingResultTypeEnum } from 'types/graphqlTypes'

/**
 * @example getDefaultOptionLabel(option) // 'Middletown, New York' or 'Middletown, New York, United States'
 */
const getDefaultOptionLabel = (option: ILocationSearchItemFragment): string => {
  const { city, country, resultType, state } = option || {}

  if (resultType === 'State') {
    return [state, country].filter(Boolean).join(', ')
  }

  return [city, state, country].filter(Boolean).join(', ')
}

export type TBaseValue = Omit<IAutocompleteBaseOption<ILocationSearchItemFragment>, 'groupIndex'>

/**
 * @note Autocomplete always uses the `id` prop to match up options
 */
const getComputedValue = (
  options: IAutocompleteBaseOption<ILocationSearchItemFragment>[],
  valueProp: TBaseValue | null
): IAutocompleteBaseOption<ILocationSearchItemFragment> | null => {
  const { id: idProp } = valueProp || {}
  const option = options.find(({ id }) => id === idProp)

  if (option) {
    return option as IAutocompleteBaseOption<ILocationSearchItemFragment>
  }

  if (valueProp) {
    return valueProp as IAutocompleteBaseOption<ILocationSearchItemFragment>
  }

  return null
}

export interface IAutocompleteLocationProps<V extends TBaseValue = TBaseValue>
  extends TAutocompleteBaseSharedProps {
  /**
   * @example [IGeocodingAllowedCountryEnum.US]
   */
  countries?: ILocationSearchQueryVariables['countries']
  /**
   * @example [IGeocodingResultTypeEnum.City]
   */
  resultTypes?: ILocationSearchQueryVariables['resultTypes']
  /**
   * @example { id: '123', text: 'New York, NY' }
   */
  value: V | null
  /**
   * @note value and onChange should always match types, if possible
   */
  onChange: (newVal: V | null) => void
  /**
   * @default 'AutocompleteLocation'
   */
  dataTest?: string
  /**
   * @default 'AutocompleteLocation'
   */
  id?: string
  /**
   * @default false
   */
  disableClearable?: boolean
  classes?: Partial<ClassNameMap<MuiAutocompleteClassKey>> | undefined
  onBlur?: FocusEventHandler<HTMLInputElement>
  /**
   * Optional custom label
   * @example ({ city, stateCode }) => [city, stateCode].filter(Boolean).join(', ') // 'New York, NY'
   * @default getDefaultOptionLabel // 'New York, NY'
   */
  getOptionLabel?: (option: ILocationSearchItemFragment) => string
  /**
   * @default {}
   */
  useAutocompleteLocationProps?: Omit<IUseAutocompleteLocationProps, 'value'>
  /**
   * If `true`, the input's text will be cleared on blur if no value is selected.
   */
  clearOnBlur?: boolean
  /**
   * If `true`, the selected option becomes the value of the input when the Autocomplete
   * loses focus unless the user chooses a different option or changes the character string in the input.
   */
  autoSelect?: boolean
  /**
   * Optional fn that returns list of options that should be prepended to the
   * queried location search options
   */
  addAdditionalOptions?: (
    inputValue: string
  ) => IAutocompleteBaseOption<ILocationSearchItemFragment>[]
}

const AutocompleteLocation = forwardRef<HTMLInputElement, IAutocompleteLocationProps>(
  (
    {
      classes,
      dataTest = 'AutocompleteLocation',
      disableClearable = false,
      onChange,
      id: idProp = 'AutocompleteLocation',
      placeholder: placeholderProp = '',
      getOptionLabel: getOptionLabelProp = getDefaultOptionLabel,
      countries,
      resultTypes = [IGeocodingResultTypeEnum.City, IGeocodingResultTypeEnum.State],
      value: valueProp,
      useAutocompleteLocationProps = {},
      clearOnBlur,
      autoSelect,
      addAdditionalOptions,
      ...props
    },
    ref
  ) => {
    const [inputValue, setInputValue] = useAutocompleteInputState()
    const { loading, refetch, options: locationSearchOptions = [] } = useAutocompleteLocation({
      value: valueProp,
      mapOption: option => {
        const { id } = option || {}
        const text = getOptionLabelProp(option)
        return {
          id: String(id),
          text,
          /**
           * Include the rest of the data as an object to use in other parts of form state
           */
          data: {
            ...option,
          },
        }
      },
      ...useAutocompleteLocationProps,
    })

    const changeHandler: TAutocompleteBaseChangeHandler = useCallback(
      (_, newVal) => {
        if (isAutocompleteBaseOption(newVal)) {
          onChange(newVal as IAutocompleteBaseOption<ILocationSearchItemFragment>)
        } else {
          onChange(null)
        }
      },
      [onChange]
    )

    /**
     * Refetch when the input value changes
     */
    useDebounce(
      () => {
        if (inputValue) {
          refetch({ variables: { searchString: inputValue, resultTypes, countries } })
        }
      },
      250,
      [inputValue]
    )

    const inputChangeHandler: TInputChangeHandler = useCallback(
      (e, newVal) => {
        /**
         * MUI triggers an onChange event with `null` value when
         * trying to press delete/backspace when there's a value
         *
         * Because this is an invalid event, we ignore it
         */
        if (e) {
          setInputValue(e, newVal)
        }
      },
      [setInputValue]
    )

    /**
     * Set input value by default
     */
    useEffect(() => {
      if (!inputValue) {
        setInputValue({} as any, valueProp?.text || '')
      }
    }, [inputValue, setInputValue, valueProp])

    const options = useMemo(() => {
      if (addAdditionalOptions) {
        return [...addAdditionalOptions(inputValue), ...locationSearchOptions]
      }
      return locationSearchOptions
    }, [addAdditionalOptions, inputValue, locationSearchOptions])

    const value = useMemo(() => getComputedValue(options, valueProp), [options, valueProp])

    return (
      <AutocompleteBase
        {...props}
        id={`${idProp}-search`}
        data-test={dataTest}
        ref={ref}
        value={value}
        onChange={changeHandler}
        options={options}
        loading={loading}
        placeholder={placeholderProp}
        loadingText='Loading...'
        filterOptionsType='fuzzy'
        onInputChange={inputChangeHandler}
        disableClearable={disableClearable}
        classes={classes}
        forcePopupIcon={false}
        clearOnBlur={clearOnBlur}
        autoSelect={autoSelect}
      />
    )
  }
)

export default AutocompleteLocation
